import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Select,
  Input,
  InputNumber,
  Button,
  Card,
  Checkbox,
  message,
  Typography,
  Space,
  Spin,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  CreateSoupRequest,
  CreateExchangeRequest,
  CreateFilterRequest,
} from '../../types/api.types';
import { useDataContext } from '../../contexts/DataContext';
import ApiService from '../../services/api';
import { MESSAGES, FILTER_LABELS } from '../../utils/constants';

const { Text } = Typography;

interface AddSoupModalProps {
  visible: boolean;
  fiatName: string;
  onCancel: () => void;
  onSuccess: () => void;
}

interface SoupFormData extends Omit<CreateSoupRequest, 'exchanges'> {
  exchanges: ExchangeFormData[];
}

interface ExchangeFormData extends Omit<CreateExchangeRequest, 'filter_buy' | 'filter_sell'> {
  useDifferentFilters: boolean;
  filter_buy: FilterFormData;
  filter_sell: FilterFormData;
}

interface FilterFormData extends CreateFilterRequest {}

const AddSoupModal: React.FC<AddSoupModalProps> = ({
  visible,
  fiatName,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [soup, setSoup] = useState<Omit<SoupFormData, 'average'> & { average: number | null }>({
    name: '',
    average: null,
    exchanges: [],
  });
  const [availableExchanges, setAvailableExchanges] = useState<string[]>([]);
  const [availableBanks, setAvailableBanks] = useState<Record<string, string[]>>({});
  const [loadingBanks, setLoadingBanks] = useState<Record<string, boolean>>({});

  const { loadFiatSettings } = useDataContext();

  useEffect(() => {
    if (visible && fiatName) {
      loadExchanges();
      resetForm();
    }
  }, [visible, fiatName]);

  const resetForm = () => {
    setSoup({
      name: '',
      average: null,
      exchanges: [],
    });
    setAvailableBanks({});
    setLoadingBanks({});
    form.resetFields();
  };

  const loadExchanges = async () => {
    try {
      const exchanges = await ApiService.getExchanges(fiatName);
      if (Array.isArray(exchanges)) {
        setAvailableExchanges(exchanges);
      } else {
        console.error('Ошибка: getExchanges не вернул массив', exchanges);
        setAvailableExchanges([]); // Устанавливаем пустой массив в случае ошибки
      }
    } catch (error) {
      console.error('Ошибка загрузки бирж:', error);
      message.error('Не удалось загрузить список бирж');
      setAvailableExchanges([]); // Устанавливаем пустой массив в случае ошибки
    }
  };

  const loadBanks = async (exchangeName: string) => {
    if (loadingBanks[exchangeName] || availableBanks[exchangeName]) return;

    setLoadingBanks(prev => ({ ...prev, [exchangeName]: true }));
    try {
      const banks = await ApiService.getBanks(fiatName, exchangeName);
      setAvailableBanks(prev => ({
        ...prev,
        [exchangeName]: Array.isArray(banks) ? banks : [],
      }));
    } catch (error) {
      console.error(`Ошибка загрузки банков для ${exchangeName}:`, error);
      message.error(`Не удалось загрузить банки для ${exchangeName}`);
    } finally {
      setLoadingBanks(prev => ({ ...prev, [exchangeName]: false }));
    }
  };

  const addExchange = () => {
    const newExchange: ExchangeFormData = {
      exchange_name: '',
      banks: [],
      useDifferentFilters: false,
      filter_buy: {
        month_order: 0,
        month_finish_rate: 0,
        max_low_single_trans_amount: 0,
        min_high_single_trans_amount: 0,
        average_size: 0,
      },
      filter_sell: {
        month_order: 0,
        month_finish_rate: 0,
        max_low_single_trans_amount: 0,
        min_high_single_trans_amount: 0,
        average_size: 0,
      },
    };

    setSoup(prev => ({
      ...prev,
      exchanges: [...prev.exchanges, newExchange],
    }));
  };

  const removeExchange = (index: number) => {
    setSoup(prev => ({
      ...prev,
      exchanges: prev.exchanges.filter((_, i) => i !== index),
    }));
  };

  const updateFilter = (
    exchangeIndex: number,
    filterType: 'buy' | 'sell',
    field: keyof FilterFormData,
    value: any
  ) => {
    setSoup(prev => ({
      ...prev,
      exchanges: prev.exchanges.map((exchange, i) => {
        if (i !== exchangeIndex) return exchange;

        const updatedExchange = { ...exchange };
        if (filterType === 'buy') {
          updatedExchange.filter_buy = { ...exchange.filter_buy, [field]: value };
          if (!exchange.useDifferentFilters) {
            updatedExchange.filter_sell = { ...exchange.filter_sell, [field]: value };
          }
        } else {
          updatedExchange.filter_sell = { ...exchange.filter_sell, [field]: value };
        }
        return updatedExchange;
      }),
    }));
  };

  const handleExchangeSelect = (exchangeIndex: number, exchangeName: string) => {
    updateExchange(exchangeIndex, 'exchange_name', exchangeName);
    loadBanks(exchangeName);
  };

  const handleToggleDifferentFilters = (exchangeIndex: number, checked: boolean) => {
    setSoup(prev => {
      const newExchanges = [...prev.exchanges];
      const exchange = newExchanges[exchangeIndex];
      exchange.useDifferentFilters = checked;

      if (checked) {
        // Копируем значения из filter_buy в filter_sell при разделении
        exchange.filter_sell = { ...exchange.filter_buy };
      } else {
        // При снятии галочки, копируем значения из filter_buy в filter_sell
         exchange.filter_sell = { ...exchange.filter_buy };
      }

      return { ...prev, exchanges: newExchanges };
    });
  };

  const updateExchange = (index: number, field: keyof Omit<ExchangeFormData, 'filter_buy' | 'filter_sell'>, value: any) => {
    setSoup(prev => ({
      ...prev,
      exchanges: prev.exchanges.map((exchange, i) =>
        i === index ? { ...exchange, [field]: value } : exchange
      ),
    }));
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      setLoading(true);

      // Проверяем, что у супа есть название и среднее значение
      if (!soup.name || soup.average === null || soup.average <= 0) {
        message.error('Заполните название супа и среднее значение');
        setLoading(false);
        return;
      }

      // Проверяем, что есть хотя бы одна биржа
      if (soup.exchanges.length === 0) {
        message.error('Добавьте хотя бы одну биржу');
        return;
      }

      // Создаем запрос для добавления супа
      const soupData: CreateSoupRequest = {
        name: soup.name,
        average: soup.average,
        exchanges: soup.exchanges.map(exchange => {
          return {
            exchange_name: exchange.exchange_name,
            banks: exchange.banks,
            filter_buy: exchange.filter_buy,
            filter_sell: exchange.useDifferentFilters ? exchange.filter_sell : exchange.filter_buy,
          };
        }),
      };

      await ApiService.createSoup(fiatName, soupData);

      message.success(MESSAGES.SUCCESS.SOUP_CREATED || 'Суп успешно добавлен');
      
      // Обновляем данные
      await loadFiatSettings(fiatName);
      
      onSuccess();
    } catch (error) {
      console.error('Ошибка добавления супа:', error);
      message.error(MESSAGES.ERROR.GENERIC);
    } finally {
      setLoading(false);
    }
  };

  const renderFilterForm = (
    exchange: ExchangeFormData,
    exchangeIndex: number,
    filterType: 'buy' | 'sell'
  ) => {
    const filter = filterType === 'buy' ? exchange.filter_buy : exchange.filter_sell;
    const title = filterType === 'buy' ? 'Фильтр для Покупки и Продажи' : 'Фильтр для Продажи';

    if (filterType === 'buy' && exchange.useDifferentFilters) {
        return (
            <Card size="small" title="Фильтр для Покупки" style={{ marginBottom: 16 }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>{FILTER_LABELS.month_order}</Text>
                    <InputNumber
                        value={filter.month_order}
                        onChange={(value) => updateFilter(exchangeIndex, 'buy', 'month_order', value || 0)}
                        style={{ width: '100%' }}
                        min={0}
                        step={1}
                    />
                    <Text>{FILTER_LABELS.month_finish_rate}</Text>
                    <InputNumber
                        value={filter.month_finish_rate}
                        onChange={(value) => updateFilter(exchangeIndex, 'buy', 'month_finish_rate', value || 0)}
                        style={{ width: '100%' }}
                        min={0}
                        max={1}
                        step={0.01}
                        placeholder="0.00 - 1.00"
                    />
                    <Text>{FILTER_LABELS.max_low_single_trans_amount}</Text>
                    <InputNumber
                        value={filter.max_low_single_trans_amount}
                        onChange={(value) => updateFilter(exchangeIndex, 'buy', 'max_low_single_trans_amount', value || 0)}
                        style={{ width: '100%' }}
                        min={0}
                        step={1}
                    />
                    <Text>{FILTER_LABELS.min_high_single_trans_amount}</Text>
                    <InputNumber
                        value={filter.min_high_single_trans_amount}
                        onChange={(value) => updateFilter(exchangeIndex, 'buy', 'min_high_single_trans_amount', value || 0)}
                        style={{ width: '100%' }}
                        min={0}
                        step={1}
                    />
                    <Text>{FILTER_LABELS.average_size}</Text>
                    <InputNumber
                        value={filter.average_size}
                        onChange={(value) => updateFilter(exchangeIndex, 'buy', 'average_size', value || 0)}
                        style={{ width: '100%' }}
                        min={0}
                        step={1}
                        precision={0}
                    />
                </Space>
            </Card>
        );
    }

    return (
      <Card size="small" title={title} style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text>{FILTER_LABELS.month_order}</Text>
          <InputNumber
            value={filter.month_order}
            onChange={(value) => updateFilter(exchangeIndex, filterType, 'month_order', value || 0)}
            style={{ width: '100%' }}
            min={0}
            step={1}
          />
          <Text>{FILTER_LABELS.month_finish_rate}</Text>
          <InputNumber
            value={filter.month_finish_rate}
            onChange={(value) => updateFilter(exchangeIndex, filterType, 'month_finish_rate', value || 0)}
            style={{ width: '100%' }}
            min={0}
            max={1}
            step={0.01}
            placeholder="0.00 - 1.00"
          />
          <Text>{FILTER_LABELS.max_low_single_trans_amount}</Text>
          <InputNumber
            value={filter.max_low_single_trans_amount}
            onChange={(value) => updateFilter(exchangeIndex, filterType, 'max_low_single_trans_amount', value || 0)}
            style={{ width: '100%' }}
            min={0}
            step={1}
          />
          <Text>{FILTER_LABELS.min_high_single_trans_amount}</Text>
          <InputNumber
            value={filter.min_high_single_trans_amount}
            onChange={(value) => updateFilter(exchangeIndex, filterType, 'min_high_single_trans_amount', value || 0)}
            style={{ width: '100%' }}
            min={0}
            step={1}
          />
          <Text>{FILTER_LABELS.average_size}</Text>
          <InputNumber
            value={filter.average_size}
            onChange={(value) => updateFilter(exchangeIndex, filterType, 'average_size', value || 0)}
            style={{ width: '100%' }}
            min={0}
            step={1}
            precision={0}
          />
        </Space>
      </Card>
    );
  };

  return (
    <Modal
      title={`Добавить новый суп для ${fiatName}`}
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={loading}
      width="90%"
      style={{ maxWidth: 800 }}
      centered
    >
      <Form form={form} layout="vertical">
        <Card title="Информация о супе" style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Input
              placeholder="Название супа"
              value={soup.name}
              onChange={(e) => setSoup(prev => ({ ...prev, name: e.target.value }))}
            />
            <InputNumber
              placeholder="Среднее значение"
              value={soup.average}
              onChange={(value) => setSoup(prev => ({ ...prev, average: value }))}
              style={{ width: '100%' }}
              min={0}
              step={1}
              precision={0}
            />
          </Space>
        </Card>

        <Card
          title="Биржи"
          extra={
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={addExchange}
              disabled={loading}
            >
              Добавить биржу
            </Button>
          }
        >
          {soup.exchanges.map((exchange, exchangeIndex) => (
            <Card
              key={exchangeIndex}
              size="small"
              title={`Биржа ${exchangeIndex + 1}`}
              extra={
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => removeExchange(exchangeIndex)}
                />
              }
              style={{ marginBottom: 16 }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Select
                  placeholder="Выберите биржу"
                  value={exchange.exchange_name || undefined}
                  onChange={(value) => handleExchangeSelect(exchangeIndex, value)}
                  style={{ width: '100%' }}
                >
                  {availableExchanges.map(exchangeName => (
                    <Select.Option key={exchangeName} value={exchangeName}>
                      {exchangeName}
                    </Select.Option>
                  ))}
                </Select>

                {exchange.exchange_name && (
                  <Spin spinning={loadingBanks[exchange.exchange_name] || false}>
                    <div>
                      <Text strong>Банки:</Text>
                      <Checkbox.Group
                        value={exchange.banks}
                        onChange={(banks) => updateExchange(exchangeIndex, 'banks', banks)}
                        style={{ width: '100%', marginTop: 8 }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {(availableBanks[exchange.exchange_name] || []).map(bank => (
                            <Checkbox key={bank} value={bank}>
                              {bank}
                            </Checkbox>
                          ))}
                        </div>
                      </Checkbox.Group>
                    </div>
                  </Spin>
                )}

                <div>
                  <Text strong>Фильтры:</Text>
                  <div style={{ marginTop: 8 }}>
                    <Checkbox
                      checked={exchange.useDifferentFilters}
                      onChange={(e) => handleToggleDifferentFilters(exchangeIndex, e.target.checked)}
                      style={{ marginBottom: 16 }}
                    >
                      Разные фильтры для покупки и продажи
                    </Checkbox>

                    {renderFilterForm(exchange, exchangeIndex, 'buy')}

                    {exchange.useDifferentFilters &&
                      renderFilterForm(exchange, exchangeIndex, 'sell')}
                  </div>
                </div>
              </Space>
            </Card>
          ))}

          {soup.exchanges.length === 0 && (
            <div style={{ textAlign: 'center', padding: '20px 0', color: '#999' }}>
              Нажмите "Добавить биржу" для начала
            </div>
          )}
        </Card>
      </Form>
    </Modal>
  );
};

export default AddSoupModal;
